import { useEffect, useState } from "react";
import { useMutation } from "convex/react";
import { api as convexApi } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import {
  DEFAULT_SESSION_STT_CONFIG,
  normalizeSessionSttConfig,
  type SessionSttConfig,
  type SessionSttProvider,
  type SttProviderKeyState,
} from "../../shared/sttConfig.ts";
import type { RoleAuth } from "../lib/auth.ts";
import { useDirtySyncedState } from "../lib/useDirtySyncedState.ts";

type SaveState = "idle" | "saving" | "saved" | "error";
interface SttDraft {
  provider: SessionSttProvider;
  speakerSensitivity: string;
}

interface SttSettingsPanelProps {
  auth: RoleAuth;
  sessionId: string;
  stt: SessionSttConfig;
  providerKeys: SttProviderKeyState;
  ingressSharedSecretConfigured: boolean;
  captureActive: boolean;
  bare?: boolean;
  hideProviderSelect?: boolean;
}

export function SttSettingsPanel({
  auth,
  sessionId,
  stt,
  providerKeys,
  ingressSharedSecretConfigured,
  captureActive,
  bare = false,
  hideProviderSelect = false,
}: SttSettingsPanelProps) {
  const saveSttConfig = useMutation(convexApi.settings.setSttConfig);
  const {
    draft,
    setDraft,
  } = useDirtySyncedState({
    value: sttDraftFromConfig(stt),
    resetKey: sessionId,
    isEqual: sttDraftEqual,
  });
  const [saveState, setSaveState] = useState<SaveState>("idle");

  useEffect(() => {
    setSaveState("idle");
  }, [sessionId]);

  const selectedProviderReady = providerKeys[draft.provider];
  const ready = ingressSharedSecretConfigured && selectedProviderReady;

  async function persist(nextProvider: SessionSttProvider, nextSensitivity: string) {
    const draft = normalizeSessionSttConfig({
      ...stt,
      mode: "live",
      provider: nextProvider,
      speechmaticsSpeakerSensitivity: numberFromDraft(nextSensitivity, stt.speechmaticsSpeakerSensitivity),
    }, DEFAULT_SESSION_STT_CONFIG);
    setSaveState("saving");
    try {
      await saveSttConfig({ auth, sessionId: sessionId as Id<"sessions">, config: draft });
      setSaveState("saved");
    } catch {
      setSaveState("error");
    }
  }

  function onProviderChange(next: SessionSttProvider) {
    setDraft((current) => ({ ...current, provider: next }));
    void persist(next, draft.speakerSensitivity);
  }
  function onSensitivityChange(next: string) {
    setDraft((current) => ({ ...current, speakerSensitivity: next }));
    void persist(draft.provider, next);
  }

  const Wrapper = bare ? "div" : "section";
  const wrapperClass = bare ? "sub stt-settings-sub" : "panel stt-settings-panel";
  const HeadingTag = bare ? "div" : "h2";
  const headingClass = bare ? "sub-head" : "";

  return (
    <Wrapper className={wrapperClass} data-testid="stt-settings-panel">
      {!bare && (
        <div className="panel-head">
          <HeadingTag className={headingClass}>Transcription</HeadingTag>
          {saveState === "saving" && <span className="gen-hint" data-testid="stt-config-save-state" role="status">saving…</span>}
          {saveState === "saved" && <span className="gen-hint ok" data-testid="stt-config-save-state" role="status">saved ✓</span>}
          {saveState === "error" && <span className="gen-hint bad" data-testid="stt-config-save-state" role="status">save failed</span>}
        </div>
      )}

      {!hideProviderSelect && (
        <div className="stt-compact-row">
          <label>
            <span className="field-label">Service</span>
            <select
            data-testid="stt-provider-select"
            value={draft.provider}
            disabled={captureActive}
            onChange={(event) => onProviderChange(event.target.value as SessionSttProvider)}
          >
              <option value="deepgram">Deepgram</option>
              <option value="speechmatics">Speechmatics</option>
              <option value="soniox">Soniox</option>
            </select>
          </label>
        </div>
      )}

      {!ready && (
        <div className="stt-warning" data-testid="stt-config-warning">
          {!ingressSharedSecretConfigured
            ? "INGRESS_SHARED_SECRET is missing in the Node/Convex environment."
            : `${providerLabel(draft.provider)} API key is not configured in the Node environment.`}
        </div>
      )}
      {captureActive && (
        <div className="stt-note" data-testid="stt-config-active-note">
          Changes apply to the next capture start. Stop and restart capture to switch provider mid-event.
        </div>
      )}

      {draft.provider === "speechmatics" && (
        <div className="stt-speechmatics-settings" data-testid="speechmatics-settings">
          <label className="stt-range">
            <span className="field-label">Speaker sensitivity: {numberFromDraft(draft.speakerSensitivity, stt.speechmaticsSpeakerSensitivity).toFixed(2)}</span>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              data-testid="speechmatics-speaker-sensitivity"
              value={draft.speakerSensitivity}
              onChange={(event) => onSensitivityChange(event.target.value)}
            />
          </label>
        </div>
      )}
    </Wrapper>
  );
}

function providerLabel(provider: SessionSttProvider): string {
  if (provider === "speechmatics") return "Speechmatics";
  if (provider === "soniox") return "Soniox";
  return "Deepgram";
}

function sttDraftFromConfig(stt: SessionSttConfig): SttDraft {
  return {
    provider: stt.provider,
    speakerSensitivity: String(stt.speechmaticsSpeakerSensitivity),
  };
}

function sttDraftEqual(a: SttDraft, b: SttDraft): boolean {
  return a.provider === b.provider &&
    a.speakerSensitivity === b.speakerSensitivity;
}

function numberFromDraft(value: string, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}
